import { useEffect, useMemo, useState } from "react";
import "instantsearch.css/themes/satellite.css";
import "./SupplierInventorySearch.css";
import "./RefinementList.css";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Text,
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon,
  ModalFooter,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from "@chakra-ui/react";
import { BsFillInboxesFill, BsX, BsCheck } from "react-icons/bs";
import { MdAddShoppingCart } from "react-icons/md";
import { SupplierQuantityCell } from "./SupplierQuantityCell";
import { AMBER_COLORS } from "./config";
import { formatDate, formatTime } from "../../utils/dateResolvers";
import { joinSupplierPartRows } from "./utils/joinSupplierPartRows";
import { isPartConfirmationEligibleSupplier } from "./utils/partConfirmationEligibility";
import { PartQuantity, SupplierPart } from "./utils/partsCalculationUtils";
import { useGetPartConfirmationResponses } from "../../views/Orders/queryHooks";
import { useGetOrderMediaConfig } from "../../views/PlaceOrderTest/queryHooks";
import type { JoinedSupplierPartRow } from "./utils/joinSupplierPartRows";

type PartConfirmationLookup = {
  quantity: number;
  status?: "confirmed" | "declined";
};

type RowSelection = {
  selected: boolean;
  quantity: number;
};

const confirmationForRow = (
  row: JoinedSupplierPartRow,
  confirmedByPartId: Map<string, PartConfirmationLookup>,
): PartConfirmationLookup | undefined =>
  confirmedByPartId.get(row.partId) || confirmedByPartId.get(row.partNumber);

const resolvedConfirmedQty = (
  confirmation?: PartConfirmationLookup,
): number | undefined => {
  if (!confirmation) {
    return undefined;
  }
  if (confirmation.status === "declined") {
    return 0;
  }
  if (confirmation.status === "confirmed") {
    return confirmation.quantity ?? 0;
  }
  return undefined;
};

const assignQtyMaxForRow = (
  row: JoinedSupplierPartRow,
  confirmation?: PartConfirmationLookup,
): number => {
  const askedQty = Math.max(0, Number(row.askedQty) || 0);
  const stockQty = Math.max(0, Number(row.stockQty) || 0);
  const cap = resolvedConfirmedQty(confirmation) ?? stockQty;
  return Math.max(0, Math.min(cap, askedQty));
};

const confirmationSortRank = (
  confirmation?: PartConfirmationLookup,
): number => {
  if (confirmation?.status === "confirmed") {
    return 0;
  }
  if (confirmation?.status === "declined") {
    return 2;
  }
  return 1;
};

const sortSupplierDetailRows = (
  rows: JoinedSupplierPartRow[],
  confirmedByPartId: Map<string, PartConfirmationLookup>,
): JoinedSupplierPartRow[] =>
  [...rows].sort((left, right) => {
    const leftConfirmation = confirmationForRow(left, confirmedByPartId);
    const rightConfirmation = confirmationForRow(right, confirmedByPartId);
    const byStatus =
      confirmationSortRank(leftConfirmation) -
      confirmationSortRank(rightConfirmation);
    if (byStatus !== 0) {
      return byStatus;
    }
    return (
      assignQtyMaxForRow(right, rightConfirmation) -
      assignQtyMaxForRow(left, leftConfirmation)
    );
  });

const getModalStyles = (isFromTopSuppliers: boolean) => ({
  headerBox: {
    bgGradient: isFromTopSuppliers
      ? `linear(to-r, white, ${AMBER_COLORS[200]})`
      : "linear(to-r, #1976f2, #5b2be0)",
    px: 6,
    py: 4,
    position: "relative" as const,
    borderTopRadius: "xl",
    borderBottom: isFromTopSuppliers ? "1px solid" : "none",
    borderColor: isFromTopSuppliers ? AMBER_COLORS[100] : "transparent",
  },
  modalBody: {
    px: 6,
    py: 4,
    flex: 1,
    overflowY: "auto" as const,
    bg: "#fbfbfb",
    fontFamily: "'Nunito','Inter', sans-serif",
    sx: {
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
  },
  tableBox: {
    border: "1px solid",
    borderColor: "gray.200",
    borderRadius: "lg",
    boxShadow: "xs",
    p: 4,
    bg: "white",
  },
  modalFooter: {
    bg: "#fffdf9",
    borderTop: "1px solid",
    borderColor: "gray.200",
    justifyContent: "flex-end",
    gap: 3,
    position: "sticky" as const,
    bottom: 0,
    zIndex: 1,
  },
});

type CustomHitsProps = {
  rows: JoinedSupplierPartRow[];
  isFromTopSuppliers: boolean;
  showConfirmedQty: boolean;
  confirmedByPartId: Map<string, PartConfirmationLookup>;
  selection: Map<string, RowSelection>;
  onTogglePart: (partId: string, checked: boolean) => void;
  onQuantityChange: (partId: string, quantity: number) => void;
};

const CustomHits = ({
  rows,
  isFromTopSuppliers,
  showConfirmedQty,
  confirmedByPartId,
  selection,
  onTogglePart,
  onQuantityChange,
}: CustomHitsProps) => {
  return (
    <>
      {rows.map((row, index) => {
        const confirmation = confirmationForRow(row, confirmedByPartId);
        const isDeclined = confirmation?.status === "declined";
        const isConfirmed = confirmation?.status === "confirmed";
        const maxQty = assignQtyMaxForRow(row, confirmation);
        const rowSelection = selection.get(row.partId);
        const isSelected = Boolean(rowSelection?.selected);
        const assignQty = rowSelection?.quantity ?? maxQty;

        return (
          <Tr
            key={row.partId}
            bg={index % 2 !== 0 ? "gray.50" : "white"}
            fontSize="sm"
          >
            <Td textAlign="center">
              <Checkbox
                isChecked={maxQty > 0 && isSelected}
                isDisabled={maxQty <= 0}
                onChange={(event) =>
                  onTogglePart(row.partId, event.target.checked)
                }
                colorScheme={isFromTopSuppliers ? "orange" : "blue"}
                aria-label={`Select ${row.partNumber}`}
              />
            </Td>
            <Td textAlign="center" color="gray.700">
              {index + 1}
            </Td>
            <Td textAlign="center" fontWeight="bold" color="gray.700">
              {row.partNumber}
            </Td>
            <Td textAlign="center" color="gray.700">
              {row.askedQty}
            </Td>
            <Td textAlign="center" color="gray.700">
              <SupplierQuantityCell part={row.supplierPart} />
            </Td>
            {showConfirmedQty ? (
              <Td
                textAlign="center"
                fontSize="xl"
                color={
                  isDeclined
                    ? "red.500"
                    : isConfirmed && (confirmation?.quantity ?? 0) > 0
                      ? "green.500"
                      : "gray.700"
                }
                fontWeight={
                  isDeclined ||
                  (isConfirmed && (confirmation?.quantity ?? 0) > 0)
                    ? "semibold"
                    : "normal"
                }
              >
                {isDeclined
                  ? 0
                  : isConfirmed && (confirmation?.quantity ?? 0) > 0
                    ? confirmation.quantity
                    : "-"}
              </Td>
            ) : null}
            <Td textAlign="center">
              <NumberInput
                value={assignQty}
                min={0}
                max={maxQty}
                isDisabled={maxQty <= 0}
                size="sm"
                maxW="96px"
                mx="auto"
                onChange={(_valueAsString, valueAsNumber) => {
                  const next = Number.isFinite(valueAsNumber)
                    ? valueAsNumber
                    : 0;
                  onQuantityChange(
                    row.partId,
                    Math.min(maxQty, Math.max(0, next)),
                  );
                }}
              >
                <NumberInputField textAlign="center" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </Td>
            <Td textAlign="center" color="gray.700">
              {row.updatedAt
                ? `${formatDate(row.updatedAt)}, ${formatTime(row.updatedAt)}`
                : "—"}
            </Td>
          </Tr>
        );
      })}
    </>
  );
};

const SupplierDetailsModal = ({
  supplierLabel,
  isOpen,
  onClose,
  preFacetedPartNumbers,
  finalHitMap,
  handleSelectListItem,
  handleAddToSelectedSupplierBucket,
  isFromTopSuppliers = false,
  onSelectionClose,
  isEstimateOrder = false,
  orderNo = "",
}: {
  supplierLabel: string;
  isOpen: boolean;
  onClose: () => void;
  preFacetedPartNumbers: PartQuantity[];
  typesenseInstantsearchAdapter: any;
  finalHitMap: Map<string, Array<SupplierPart & { id?: string }>>;
  handleSelectListItem: (
    item: { _id: string; supplierName: string },
    supplierParts?: any[],
  ) => void;
  handleAddToSelectedSupplierBucket: (
    item: { _id: string; supplierName: string },
    supplierParts?: any[],
  ) => void;
  onSelectionClose: () => void;
  isFromTopSuppliers?: boolean;
  isEstimateOrder?: boolean;
  orderNo?: string;
}) => {
  const finalSupplierPartsMap = useMemo(() => {
    return finalHitMap.get(supplierLabel) ?? [];
  }, [finalHitMap, supplierLabel]);
  const supplierId = supplierLabel.split("::")[1] ?? "";
  const supplierName = supplierLabel.split("::")[0] ?? supplierLabel;
  const { data: mobileConfigs } = useGetOrderMediaConfig();
  const showConfirmedQty = isPartConfirmationEligibleSupplier(
    supplierId,
    mobileConfigs?.part_confirmation_eligible_supplier_ids,
  );
  const { data: confirmationData } = useGetPartConfirmationResponses(
    orderNo,
    supplierId,
    isOpen,
    showConfirmedQty,
  );
  const confirmedByPartId = useMemo(() => {
    const map = new Map<string, PartConfirmationLookup>();
    (confirmationData?.parts ?? []).forEach(
      (part: {
        partId?: string;
        partNumber?: string;
        quantity?: number;
        status?: "confirmed" | "declined";
      }) => {
        const value = {
          quantity: part.quantity ?? 0,
          status: part.status,
        };
        if (part.partId) map.set(part.partId, value);
        if (part.partNumber) map.set(part.partNumber, value);
      },
    );
    return map;
  }, [confirmationData]);

  const rows = useMemo(
    () =>
      sortSupplierDetailRows(
        joinSupplierPartRows(preFacetedPartNumbers, finalSupplierPartsMap),
        confirmedByPartId,
      ),
    [preFacetedPartNumbers, finalSupplierPartsMap, confirmedByPartId],
  );

  const [selection, setSelection] = useState<Map<string, RowSelection>>(
    () => new Map(),
  );

  useEffect(() => {
    if (!isOpen) {
      setSelection(new Map());
      return;
    }
    setSelection(() => {
      const next = new Map<string, RowSelection>();
      rows.forEach((row) => {
        const maxQty = assignQtyMaxForRow(
          row,
          confirmationForRow(row, confirmedByPartId),
        );
        next.set(row.partId, {
          selected: maxQty > 0,
          quantity: maxQty,
        });
      });
      return next;
    });
  }, [isOpen, rows, confirmedByPartId]);

  const selectablePartIds = useMemo(
    () =>
      rows
        .filter(
          (row) =>
            assignQtyMaxForRow(
              row,
              confirmationForRow(row, confirmedByPartId),
            ) > 0,
        )
        .map((row) => row.partId),
    [rows, confirmedByPartId],
  );
  const selectedCount = selectablePartIds.filter(
    (id) => selection.get(id)?.selected,
  ).length;
  const allSelected =
    selectablePartIds.length > 0 && selectedCount === selectablePartIds.length;
  const someSelected = selectedCount > 0 && !allSelected;
  const addableCount = rows.filter((row) => {
    const rowSelection = selection.get(row.partId);
    return Boolean(rowSelection?.selected) && (rowSelection?.quantity ?? 0) > 0;
  }).length;

  const toggleAll = () => {
    setSelection((prev) => {
      const next = new Map(prev);
      const selectAll = !allSelected;
      rows.forEach((row) => {
        const current = next.get(row.partId);
        const maxQty = assignQtyMaxForRow(
          row,
          confirmationForRow(row, confirmedByPartId),
        );
        next.set(row.partId, {
          selected: selectAll && maxQty > 0,
          quantity: current?.quantity ?? maxQty,
        });
      });
      return next;
    });
  };

  const onTogglePart = (partId: string, checked: boolean) => {
    setSelection((prev) => {
      const next = new Map(prev);
      const current = next.get(partId);
      const row = rows.find((item) => item.partId === partId);
      if (!current || !row) {
        return prev;
      }
      const maxQty = assignQtyMaxForRow(
        row,
        confirmationForRow(row, confirmedByPartId),
      );
      if (maxQty <= 0) {
        next.set(partId, { selected: false, quantity: 0 });
        return next;
      }
      next.set(partId, { ...current, selected: checked });
      return next;
    });
  };

  const onQuantityChange = (partId: string, quantity: number) => {
    setSelection((prev) => {
      const next = new Map(prev);
      const current = next.get(partId);
      if (!current) {
        return prev;
      }
      next.set(partId, { selected: quantity > 0, quantity });
      return next;
    });
  };

  const handleAddToPartBucket = () => {
    const selectedParts = rows.flatMap((row) => {
      const rowSelection = selection.get(row.partId);
      if (!rowSelection?.selected || rowSelection.quantity <= 0) {
        return [];
      }
      return [
        {
          ...row.supplierPart,
          rootPartNumber: row.rootPartNumber,
          familyQuantity: rowSelection.quantity,
        },
      ];
    });
    if (selectedParts.length === 0) {
      return;
    }
    handleAddToSelectedSupplierBucket(
      {
        _id: supplierId,
        supplierName,
      },
      selectedParts,
    );
    onClose();
  };

  const modalStyles = getModalStyles(isFromTopSuppliers);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      scrollBehavior="inside"
      isCentered
    >
      <ModalOverlay />
      <ModalContent
        borderRadius="xl"
        overflow="hidden"
        maxHeight="85vh"
        display="flex"
        flexDirection="column"
        boxShadow={"md"}
      >
        {/* Header */}
        <Box {...modalStyles.headerBox}>
          <ModalCloseButton
            color={"white"}
            top={3}
            right={4}
            _hover={{
              background: "white",
              color: isFromTopSuppliers ? AMBER_COLORS[600] : "blue.500",
            }}
          />
          <Box>
            <Text
              fontSize="lg"
              fontWeight="bold"
              color={isFromTopSuppliers ? AMBER_COLORS[700] : "white"}
            >
              {supplierLabel.split("::")[0]}
            </Text>
            <Text
              fontSize="sm"
              fontWeight="500"
              color={
                isFromTopSuppliers
                  ? AMBER_COLORS[600]
                  : "rgba(255, 255, 255, 0.64)"
              }
            >
              Detailed Information
            </Text>
          </Box>
        </Box>

        {/* Body */}
        <ModalBody {...modalStyles.modalBody}>
          <Box {...modalStyles.tableBox}>
            <Text
              fontSize="md"
              fontWeight="semibold"
              mb={3}
              display="flex"
              alignItems="center"
              gap={2}
              color={"gray.700"}
            >
              <Icon
                as={BsFillInboxesFill}
                color={isFromTopSuppliers ? AMBER_COLORS[600] : "blue.500"}
                boxSize={4}
              />
              Part Numbers and Quantities
            </Text>

            <Box overflowX="auto" fontFamily="'Nunito','Inter', sans-serif">
              <Table size="md" variant="unstyled">
                <Thead bg={"gray.100"}>
                  <Tr>
                    <Th textAlign="center" fontSize="md" color={"gray.600"}>
                      <Checkbox
                        isChecked={allSelected}
                        isIndeterminate={someSelected}
                        isDisabled={selectablePartIds.length === 0}
                        onChange={toggleAll}
                        colorScheme={isFromTopSuppliers ? "orange" : "blue"}
                        aria-label="Select all parts"
                      />
                    </Th>
                    <Th textAlign="center" fontSize="md" color={"gray.600"}>
                      #
                    </Th>
                    <Th textAlign="center" fontSize="md" color={"gray.600"}>
                      Part #
                    </Th>
                    <Th textAlign="center" fontSize="md" color={"gray.600"}>
                      Ordered Quantity
                    </Th>
                    <Th textAlign="center" fontSize="md" color={"gray.600"}>
                      Supplier Quantity
                    </Th>
                    {showConfirmedQty ? (
                      <Th textAlign="center" fontSize="md" color={"gray.600"}>
                        Confirmed Qty
                      </Th>
                    ) : null}
                    <Th textAlign="center" fontSize="md" color={"gray.600"}>
                      Assign Qty
                    </Th>
                    <Th textAlign="center" fontSize="md" color={"gray.600"}>
                      Last Updated
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <CustomHits
                    rows={rows}
                    isFromTopSuppliers={isFromTopSuppliers}
                    showConfirmedQty={showConfirmedQty}
                    confirmedByPartId={confirmedByPartId}
                    selection={selection}
                    onTogglePart={onTogglePart}
                    onQuantityChange={onQuantityChange}
                  />
                </Tbody>
              </Table>
            </Box>
          </Box>
        </ModalBody>

        {/* Footer */}
        <ModalFooter {...modalStyles.modalFooter}>
          <Button
            variant="ghost"
            color={isFromTopSuppliers ? AMBER_COLORS[700] : "gray.700"}
            leftIcon={<Icon as={BsX} />}
            onClick={onClose}
            _hover={{
              bg: isFromTopSuppliers ? AMBER_COLORS[50] : "gray.100",
              color: isFromTopSuppliers ? AMBER_COLORS[600] : "gray.800",
            }}
            fontSize={{
              base: "xs",
              "2xl": "md",
            }}
          >
            Close
          </Button>
          <Button
            variant="outline"
            colorScheme={isFromTopSuppliers ? "orange" : "blue"}
            leftIcon={<Icon as={MdAddShoppingCart} />}
            onClick={handleAddToPartBucket}
            isDisabled={addableCount === 0}
            fontSize={{
              base: "xs",
              "2xl": "md",
            }}
          >
            Add to part bucket
          </Button>
          <Button
            backgroundSize="200%"
            transition="background-position 0.3s ease"
            backgroundPosition="left center"
            color={isFromTopSuppliers ? AMBER_COLORS[700] : "white"}
            bgGradient={
              isFromTopSuppliers
                ? `linear(to-l,${AMBER_COLORS[200]},  ${AMBER_COLORS[50]}, ${AMBER_COLORS[200]})`
                : "linear(to-r,#5b2be0, #1976f2, #5b2be0)"
            }
            leftIcon={<Icon as={BsCheck} />}
            fontSize={{
              base: "xs",
              "2xl": "md",
            }}
            _hover={{
              backgroundPosition: "right center",
            }}
            onClick={() => {
              const supplierId = supplierLabel.split("::")[1];
              const supplierName = supplierLabel.split("::")[0];
              handleSelectListItem(
                {
                  _id: supplierId,
                  supplierName: supplierName,
                },
                finalHitMap.get(supplierLabel)
              );
              onSelectionClose();
              onClose();
            }}
          >
            {isEstimateOrder ? "View Estimate" : "Select Supplier"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
export default SupplierDetailsModal;
